import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { AlertCircle, Check, X, RefreshCw } from 'lucide-react';
import './StaleAdPrompt.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'stale_ad_prompt_dismissed';

/**
 * StaleAdPrompt - Modal prompt asking seller if their stale ads are still available
 * 
 * Props:
 * - ads: Array of stale ads from /api/users/me/stale-ads
 * - onRefresh: Callback when user confirms or marks sold
 * - locale: 'en' | 'ar'
 */
export function StaleAdPrompt({ ads = [], onRefresh, locale = 'en' }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const isArabic = locale === 'ar';
  const token = localStorage.getItem('token');
  
  // Check if we should show the prompt
  useEffect(() => {
    if (ads.length > 0) {
      // Check if dismissed within last 48 hours
      const dismissedData = localStorage.getItem(DISMISS_KEY);
      if (dismissedData) {
        try {
          const { timestamp, adIds } = JSON.parse(dismissedData);
          const hoursSinceDismiss = (Date.now() - timestamp) / (1000 * 60 * 60);
          
          // If < 48 hours and same ads, don't show
          if (hoursSinceDismiss < 48) {
            const currentAdIds = ads.map(a => a.ad_id).sort().join(',');
            if (adIds === currentAdIds) {
              return;
            }
          }
        } catch (e) {
          // Invalid data, clear it
          localStorage.removeItem(DISMISS_KEY);
        }
      }
      
      setIsOpen(true);
    }
  }, [ads]);
  
  const currentAd = ads[currentAdIndex];
  
  const handleConfirmAvailable = async () => {
    if (!currentAd || !token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ads/${currentAd.ad_id}/confirm-available`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        toast.success(isArabic ? 'تم تحديث الإعلان' : 'Ad refreshed');
        moveToNextAd();
        if (onRefresh) onRefresh();
      } else {
        toast.error(isArabic ? 'فشل في التحديث' : 'Failed to refresh');
      }
    } catch (err) {
      console.error('Confirm available error:', err);
      toast.error(isArabic ? 'حدث خطأ' : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };
  
  const handleMarkSold = async () => {
    if (!currentAd || !token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ads/${currentAd.ad_id}/mark-sold`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        toast.success(isArabic ? 'تم تحديد الإعلان كمباع' : 'Marked as sold');
        moveToNextAd();
        if (onRefresh) onRefresh();
      } else {
        toast.error(isArabic ? 'فشل في التحديث' : 'Failed to update');
      }
    } catch (err) {
      console.error('Mark sold error:', err);
      toast.error(isArabic ? 'حدث خطأ' : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };
  
  const moveToNextAd = () => {
    if (currentAdIndex < ads.length - 1) {
      setCurrentAdIndex(prev => prev + 1);
    } else {
      // All done
      setIsOpen(false);
    }
  };
  
  const handleDismiss = () => {
    // Store dismiss timestamp and ad IDs
    const adIds = ads.map(a => a.ad_id).sort().join(',');
    localStorage.setItem(DISMISS_KEY, JSON.stringify({
      timestamp: Date.now(),
      adIds
    }));
    setIsOpen(false);
  };
  
  if (!currentAd) return null;
  
  const daysOld = currentAd.days_since_refresh || 0;
  
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="stale-ad-prompt-dialog" dir={isArabic ? 'rtl' : 'ltr'}>
        <DialogHeader>
          <div className="stale-prompt-icon">
            <AlertCircle size={28} />
          </div>
          <DialogTitle className="stale-prompt-title">
            {isArabic ? 'هل هذا الإعلان ما زال متاحاً؟' : 'Is this still available?'}
          </DialogTitle>
          <DialogDescription className="stale-prompt-description">
            {isArabic 
              ? `إعلانك "${currentAd.title}" لم يتم تحديثه منذ ${daysOld} يوم.`
              : `Your ad "${currentAd.title}" hasn't been updated in ${daysOld} days.`}
          </DialogDescription>
        </DialogHeader>
        
        {/* Ad Preview */}
        <div className="stale-ad-preview">
          {currentAd.images?.[0] && (
            <img 
              src={currentAd.images[0].startsWith('http') ? currentAd.images[0] : `${API_URL}${currentAd.images[0]}`}
              alt={currentAd.title}
              className="stale-ad-image"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <div className="stale-ad-info">
            <h4 className="stale-ad-title">{currentAd.title}</h4>
            <p className="stale-ad-price">
              {currentAd.currency || 'SDG'} {currentAd.price?.toLocaleString()}
            </p>
          </div>
        </div>
        
        {/* Progress indicator */}
        {ads.length > 1 && (
          <div className="stale-progress">
            {isArabic 
              ? `${currentAdIndex + 1} من ${ads.length}` 
              : `${currentAdIndex + 1} of ${ads.length}`}
          </div>
        )}
        
        {/* Actions */}
        <div className="stale-prompt-actions">
          <button
            onClick={handleConfirmAvailable}
            disabled={loading}
            className="stale-btn stale-btn-primary"
            data-testid="confirm-available-btn"
          >
            <Check size={18} />
            <span>{isArabic ? 'نعم، ما زال متاحاً' : 'Yes, still available'}</span>
          </button>
          
          <button
            onClick={handleMarkSold}
            disabled={loading}
            className="stale-btn stale-btn-secondary"
            data-testid="mark-sold-prompt-btn"
          >
            <RefreshCw size={18} />
            <span>{isArabic ? 'تم البيع' : 'Mark as sold'}</span>
          </button>
          
          <button
            onClick={handleDismiss}
            disabled={loading}
            className="stale-btn stale-btn-ghost"
            data-testid="dismiss-prompt-btn"
          >
            <X size={16} />
            <span>{isArabic ? 'ليس الآن' : 'Not now'}</span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default StaleAdPrompt;
